import { z } from 'zod';
import type { AstroSummary, TimeConfidence } from './astrology.js';
import type { MediaRef } from './media.js';

export type ConnectionIntent =
  | 'romance'
  | 'friendship'
  | 'professional'
  | 'founder'
  | 'mentorship'
  | 'study'
  | 'creative'
  | 'local-activities'
  | 'astrology-discussion';

export const CONNECTION_INTENTS: ConnectionIntent[] = [
  'romance',
  'friendship',
  'professional',
  'founder',
  'mentorship',
  'study',
  'creative',
  'local-activities',
  'astrology-discussion',
];

export type AgeBand = '18-24' | '25-34' | '35-44' | '45-54' | '55+';

export type LifeStage =
  | 'student'
  | 'early-career'
  | 'establishing'
  | 'established'
  | 'transitioning';

export type LocaleId = 'en' | 'hi' | 'hinglish' | 'bn';

export type Gender = 'woman' | 'man' | 'nonbinary' | 'prefer-not-to-say';

/**
 * Which kundli-matching tradition scores this user's matches.
 *
 * 'north' → Ashtakoota (the eight kootas out of 36, guna milan).
 * 'south' → Dasa Porutham (the ten poruthams out of 10).
 *
 * NOT a paid feature and never will be. Picking the tradition you were raised
 * in is identity, not volume — a Tamil or Malayali user seeing their match
 * graded by the northern 36 is being told their reckoning is the premium one.
 * The Pro line sits at `matchFilters`, which shrinks the candidate pool and
 * therefore costs reads; this one only changes which arithmetic runs.
 *
 * Absent means 'north'. Astrally ships India-first and Ashtakoota is the more
 * widely recognised system, so it is the default rather than the choice being
 * forced at onboarding.
 */
export type MatchingRegion = 'north' | 'south';

export const MATCHING_REGIONS: MatchingRegion[] = ['north', 'south'];

export const DEFAULT_MATCHING_REGION: MatchingRegion = 'north';

/**
 * Optional discovery narrowing, applied to the CANDIDATE POOL rather than to
 * the ranking — see functions/src/matching/matchFilters.ts for the semantics
 * and for exactly how coarse the distance measurement is.
 *
 * Pro-only, and the reason is cost rather than value gating: a filter removes
 * candidates before they are scored, so the pool has to be widened (more reads)
 * to refill the deck. A Base account may hold these values — the UI lets it set
 * them so the upgrade has something concrete to promise — but discovery ignores
 * them and says so with `DiscoveryFeedResult.filtersRequirePro`.
 *
 * Every field is optional; an object where all three are absent means "no
 * filter" and is treated as unset.
 */
export interface MatchFilters {
  /** Inclusive lower bound on the candidate's age, in years. Never below 18. */
  ageMin?: number;
  /** Inclusive upper bound on the candidate's age, in years. */
  ageMax?: number;
  /** Maximum city-to-city distance in kilometres. */
  maxKm?: number;
}

export const matchingRegionSchema = z.enum(['north', 'south']);

/**
 * Runtime validation for `UserDoc.matchFilters`.
 *
 * Needed because firestore.rules lets a client write its own profile document
 * (everything except the three consent keys), so these two fields arrive
 * unvalidated. Discovery parses before it filters: a negative maxKm or an
 * ageMin of 4 must not become a query the server honours.
 *
 * Bounds are deliberate. 18 is the product's floor and appears here as well as
 * in `ageConfirmed`; 120 keeps ageMax finite; 20000 km is half the earth's
 * circumference, past which the filter cannot exclude anybody.
 */
export const matchFiltersSchema = z
  .object({
    ageMin: z.number().int().min(18).max(120).optional(),
    ageMax: z.number().int().min(18).max(120).optional(),
    maxKm: z.number().min(1).max(20000).optional(),
  })
  // An inverted band would silently match nobody; treat it as malformed input.
  .refine((f) => f.ageMin === undefined || f.ageMax === undefined || f.ageMin <= f.ageMax, {
    message: 'ageMin must not exceed ageMax',
  });

export type MatchFiltersParsed = z.infer<typeof matchFiltersSchema>;

/**
 * Public-ish user document (Firestore `users/{uid}`) — the SOCIAL profile.
 * Contains ONLY what another matched user may eventually see, plus
 * discovery metadata. Never exact birth data, never money/entitlement fields
 * (those live server-only in `entitlements/{uid}`).
 */
export interface UserDoc {
  uid: string;
  displayName: string;
  /**
   * PROFILE PICTURE. Replaces the old `photoPath?: string`, which was declared
   * but never written by anything in the product — a bare path could not say
   * how large the image is or carry the blur used while it loads, and having
   * two fields that both mean "the picture" is how they drift.
   *
   * Deliberately small: 256px WebP under 30KB (AVATAR_MAX_PX /
   * AVATAR_MAX_BYTES), resized on the device before upload. Discovery renders
   * dozens of these per session and egress — not storage — is the bill.
   *
   * `path` is a Storage object path, never a download URL: a download URL
   * carries a token that bypasses Storage rules. Fetch it with getBlob/getBytes
   * so every request is evaluated against `storage.rules`.
   *
   * OPTIONAL, and that is a product decision rather than an oversight. The
   * picture is required to JOIN the social layer (socialSetEnabled refuses the
   * off→on transition without one) because discovery is the only thing that
   * renders it. An account that only ever uses charts and Ally is never asked
   * for a photo — demanding one would be friction, storage and an identity for
   * nothing, and it would contradict private-by-default.
   */
  avatar?: MediaRef;
  bio?: string;
  ageBand: AgeBand;
  /** Display age (years). Optional; shown on discovery cards when set. */
  age?: number;
  gender?: Gender;
  /** Broad region for matching practicality, e.g. "in-mh-mumbai" or "in-dl". */
  region: string;
  city?: string;
  /** When true, show only the broad city — never locality. */
  broadCityOnly?: boolean;
  languages: LocaleId[];
  occupation?: string;
  education?: string;
  intents: ConnectionIntent[];
  /** One primary intention; `intents` remains the full openness set. */
  primaryIntention?: ConnectionIntent;
  secondaryIntentions?: ConnectionIntent[];
  lifeStage?: LifeStage;
  /**
   * Stable `Interest.id` keys from the curated taxonomy in
   * `data/interests.ts` (INTERESTS / interestById). Store ids only — labels
   * and category emoji live in the taxonomy so copy can change without
   * migrating profiles. Matching compares these via `interestOverlap()`.
   */
  interests: string[];
  /**
   * Which kundli system grades THIS user's view of a match. Free for everyone;
   * absent means DEFAULT_MATCHING_REGION.
   */
  matchingRegion?: MatchingRegion;
  /**
   * Discovery narrowing. Stored for every tier, honoured only for Pro — see
   * MatchFilters.
   */
  matchFilters?: MatchFilters;
  /** 0..1 — drives discovery profileQuality signal. */
  profileCompletion?: number;
  verification?: { photo: boolean };
  /** Hide last-active band from others. */
  hideActiveStatus?: boolean;

  /**
   * MASTER SWITCH for the entire social layer. **False by default — every
   * account is private from day one.** While false there is no social
   * component at all: the profile is invisible to everyone, no compatibility
   * is computed against it, no recommendations are generated, and connection
   * requests can be neither sent nor received. Only the user can turn it on,
   * and only through the explicit opt-in flow (server-controlled: clients
   * cannot write this field directly).
   */
  socialEnabled: boolean;
  /** When the user opted in. Absent means they never have. */
  socialConsentAtISO?: string;

  /**
   * Whether to appear in others' discovery. Meaningful only while
   * socialEnabled is true, and always forced false when it is not — this is
   * the field discovery queries filter on.
   */
  discoverable: boolean;
  /** A temporary pause *within* an opted-in account. */
  recommendationsPaused: boolean;
  ageConfirmed: boolean;
  createdAtISO: string;
  lastActiveAtISO: string;

  /**
   * Which emails this account wants. Absent means "never chosen" — see
   * digestEnabled() for what that resolves to.
   */
  emailPrefs?: EmailPrefs;
  /**
   * Capability token for the unauthenticated unsubscribe link, minted lazily
   * the first time an email carrying one is composed (functions/src/email).
   * Server-written; the client never needs it.
   *
   * It lives on this document rather than under `private/` so the unsubscribe
   * endpoint can find it with no sign-in, which means it is READABLE by other
   * signed-in users once `socialEnabled` is true — Firestore cannot hide one
   * field from a document read. What that token can do is bounded to "turn the
   * digest off"; the exposure and the migration out of it are written up in
   * functions/src/email/emailUnsubscribe.ts.
   */
  emailUnsubToken?: string;
}

/** `users/{uid}.emailPrefs`. One flag today; more will land beside it. */
export interface EmailPrefs {
  digest: boolean;
}

/**
 * Whether the daily digest should go out, for a member who has never touched
 * the setting.
 *
 * Pro members are opted IN by default because the digest is part of what Pro
 * is; everyone else is opted out because they were never offered it. An
 * explicit choice always wins over both. Shared with the app so the toggle in
 * Profile → Notifications shows the same state the sender will act on — the
 * sender re-checks Pro tier at send time regardless.
 */
export function digestEnabled(prefs: EmailPrefs | undefined, isPro: boolean): boolean {
  return prefs?.digest ?? isPro;
}

/**
 * Machine-readable reason on the `socialSetEnabled` error when the account is
 * trying to JOIN the social layer without a profile picture.
 *
 * The client keys its "add a picture" state off this rather than off the
 * message text, which is user-facing copy and will be translated. It is a
 * distinct code from every other opt-in refusal because it is the only one the
 * screen can fix in place, with the picker it already renders.
 */
export const SOCIAL_AVATAR_REQUIRED_REASON = 'social-avatar-required';

/**
 * Private profile (Firestore `users/{uid}/private/profile`).
 * Owner-read only. Exact birth data stays on-device by default; this doc
 * stores sync metadata and consents, not raw birth details.
 */
export interface PrivateProfileDoc {
  uid: string;
  email?: string;
  timeConfidence: TimeConfidence;
  /** Whether the user opted to back up encrypted birth details (off by default). */
  birthBackupEnabled: boolean;
  matchingConsentAtISO?: string;
  locale: LocaleId;
  astroSummary?: AstroSummary;
  pushToken?: string;
}

/** Server-maintained score profile (Firestore `userScores/{uid}`, server-write-only). */
export interface UserScoreProfileDoc {
  uid: string;
  scoringVersion: string;
  /** All 0..1 */
  dimensions: {
    communicationDirectness: number;
    conversationDepth: number;
    humour: number;
    emotionalOpenness: number;
    socialEnergy: number;
    replyConsistency: number;
    profileCompleteness: number;
    reliability: number;
  };
  trust: {
    /** 0..1, decremented by verified reports; not user-visible. */
    level: number;
    flags: string[];
  };
  updatedAtISO: string;
}

/**
 * Server-only entitlement state (`entitlements/{uid}`) — the single place
 * money-adjacent state lives. Clients read their own doc; ALL writes happen
 * in Cloud Functions transactions. Purchases on Android unlock the same
 * account on web because this is keyed by uid, not device.
 */
export interface EntitlementDoc {
  uid: string;
  /** Effective tier: pro implies base access. */
  tier: 'none' | 'base' | 'pro';
  baseEntitled: boolean;
  basePurchasedAtISO?: string;
  proStatus: 'none' | 'active' | 'expired';
  proProductId?: string;
  proExpiresAtISO?: string;
  /**
   * The same instant as `proExpiresAtISO`, in epoch milliseconds, FOR
   * storage.rules — which is the media paywall and has no date parser. A rule
   * can compare a number against `request.time.toMillis()` and can do nothing
   * at all with an ISO string.
   *
   * Two fields for one fact is duplication with a reason. Every server-side
   * reader uses effectiveTier(), which parses the ISO; the rule cannot call a
   * function, so it needs the number. Keep them written together — see
   * proExpiryFields() in functions/src/lib/entitlements.ts, which derives this
   * from the ISO precisely so the two cannot disagree.
   *
   * Optional because documents written before this field existed do not have
   * it, and the rule defaults an absent value to 0 (= "no expiry recorded",
   * which passes). Those accounts keep the old behaviour until their
   * entitlement is next written.
   */
  proExpiresAtMs?: number;
  /** LIFETIME outgoing connection requests consumed on the Base plan. */
  baseOutgoingUsed: number;
  /** Rolling-day outgoing counter for Pro ("YYYYMMDD" key + count). */
  proDailyKey?: string;
  proDailyUsed?: number;
  /** Mirror of the credit ledger (ledger is canonical). */
  aiCreditBalance: number;
  /** Daily free standard-AI messages consumed (Base trial allowance). */
  aiTrialDayKey?: string;
  aiTrialUsed?: number;
  updatedAtISO: string;
}

export interface BlockDoc {
  blockedUid: string;
  createdAtISO: string;
}

export type ReportReason =
  | 'harassment'
  | 'spam'
  | 'fake-profile'
  | 'inappropriate-content'
  | 'safety-concern'
  | 'other';

export interface ReportDoc {
  id: string;
  reporterUid: string;
  subjectUid: string;
  reason: ReportReason;
  context?: string;
  conversationId?: string;
  status: 'open' | 'reviewing' | 'actioned' | 'dismissed';
  createdAtISO: string;
}
